import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { MessageDescriptorPropType } from '../../../../prop-types/intl';
import { FancyStateIcon } from 'ui-components/components/fancy-state-icon';
import { Button } from 'ui-components/components/button';
import { testId } from 'ui-components/services/testid-service';
import { BulkErrorOrWarningDataTable } from '../bulk-error-or-warning-data-table';
import style from './bulk-error-or-warning.module.less';

const BulkErrorOrWarningComponent = ({
  id = '',
  type = 'error',
  message = '',
  isExpanded = false,
  additionalAction = {
    text: {
      id: '',
      defaultMessage: '',
    },
  },
  expandError,
  removeError,
  className = '',
  isRemovable = true,
  data = [],
  dataType,
  removeItemFromSelection,
  onOrderNumberClick,
  onShipmentNumberClick,
}) => {
  return (
    <div className={classnames(style['bulk-error-or-warning'], style[type], className)}>
      <div className={style['error-header']}>
        <div className={style['error-indicator']}>
          <div className={style['error-indicator-icon']}>
            <FancyStateIcon state={type} />
          </div>
          <div className={style['error-indicator-name']}>
            {type === 'error' && <FormattedMessage defaultMessage='Error' description='bulkErrorOrWarning.error' id='BFKEh6' />}
            {type === 'warning' && <FormattedMessage defaultMessage='Warning' description='bulkErrorOrWarning.warning' id='SUkXyM' />}
          </div>
        </div>
        <div className={style['error-data']}>
          <div className={style['error-description']}>{message}</div>
          <div className={style['error-actions']}>
            {additionalAction && additionalAction.handler && (
              <Button onClick={() => additionalAction.handler(id)} theme='link'>
                <FormattedMessage {...additionalAction.text} />
              </Button>
            )}
            {isRemovable && (
              <Button onClick={() => removeError(id)} theme='link'>
                <FormattedMessage defaultMessage='Remove All' description='bulkErrorOrWarning.removeAll' id='8/SHpg' />
              </Button>
            )}
          </div>
        </div>
        <div className={style['expander-wrapper']}>
          <Button
            className={classnames(style['expander-button'], {
              [style.expanded]: isExpanded,
            })}
            onClick={() => expandError(id)}
            testId='error-toggle'
            theme='unstyled'
          >
            <FontAwesomeIcon icon={faCaretDown} />
          </Button>
        </div>
      </div>
      {isExpanded && (
        <div className={style['error-body']} {...testId('error-body')}>
          <BulkErrorOrWarningDataTable
            className={className}
            data={data}
            dataType={dataType}
            isRemovable={isRemovable}
            onOrderNumberClick={onOrderNumberClick}
            onShipmentNumberClick={onShipmentNumberClick}
            removeItemFromSelection={removeItemFromSelection}
          />
        </div>
      )}
    </div>
  );
};

BulkErrorOrWarningComponent.propTypes = {
  id: PropTypes.string,
  type: PropTypes.oneOf(['error', 'warning']),
  message: PropTypes.node,
  isExpanded: PropTypes.bool,
  dataType: PropTypes.oneOf(['salesOrder', 'fulfillmentPlan', 'fulfillmentPlanWithoutSalesOrder', 'batchedFulfillmentPlan']).isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      salesOrderId: PropTypes.string,
      salesOrderNumber: PropTypes.string,
      fulfillmentPlanId: PropTypes.string,
      error: PropTypes.shape({
        text: PropTypes.string,
        handler: PropTypes.func,
      }),
    }),
  ),
  additionalAction: PropTypes.shape({
    text: MessageDescriptorPropType.isRequired,
    handler: PropTypes.func,
  }),
  expandError: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  removeItemFromSelection: PropTypes.func.isRequired,
  onOrderNumberClick: PropTypes.func.isRequired,
  onShipmentNumberClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  isRemovable: PropTypes.bool,
};

export const BulkErrorOrWarning = memo(BulkErrorOrWarningComponent);
